// Reports — the statements the accountant files from. Every report has a
// header with the entity, the range and the date prepared; CSV and Print.
// Drill-ins (trial balance → general ledger) use query params and show Back.
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBooksAccess } from '../../contexts/AuthContext';
import { Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day } from '../../lib/format';
import { FAR_PAST, members as membersApi, reports as reportsApi, settings as settingsApi } from '../../lib/ledger/api';
import { ACCOUNT_TYPE_LABELS, MEMBERS, SOURCE_LABELS } from '../../lib/ledger/types';
import type { Account, AccountType, EntitySettings, LedgerLine, Member, MemberSettings, TrialBalanceRow } from '../../lib/ledger/types';
import {
  addDays, build1065, buildBalanceSheet, buildCashFlow, buildProfitLoss, buildTrialBalance, priorPeriod, runningBalances,
} from '../../lib/ledger/reports';
import type { BalanceSheet, BalanceSheetSummary, CashFlowSection, Form1065, ProfitLoss, Section as PlSection } from '../../lib/ledger/reports';
import { BackTo, Money, PrintButton, ReadOnlyNote, downloadCsv, today, useAccounts, yearStart } from './shared';

function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

type ReportKey = 'pl' | 'bs' | 'cf' | 'tb' | 'gl' | 'capital' | 'f1065';
const REPORTS: { key: ReportKey; label: string }[] = [
  { key: 'pl', label: 'Profit and loss' },
  { key: 'bs', label: 'Balance sheet' },
  { key: 'cf', label: 'Cash flow' },
  { key: 'tb', label: 'Trial balance' },
  { key: 'gl', label: 'General ledger' },
  { key: 'capital', label: 'Member capital' },
  { key: 'f1065', label: 'Form 1065 summary' },
];

const money = (c: number) => (c / 100).toFixed(2);
const thisYear = () => new Date().getFullYear();
const yearsBack = (n = 6) => Array.from({ length: n }, (_, i) => thisYear() - i);

type Entity = { name: string; members: Record<Member, string> };

export function ReportsTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const [params, setParams] = useSearchParams();
  const settings = useLoad(() => settingsApi.all(), []);
  const entity = useMemo<Entity>(() => {
    const e = (settings.data?.entity as Partial<EntitySettings> | undefined) ?? {};
    const m = (settings.data?.members as Partial<MemberSettings> | undefined) ?? {};
    return {
      name: e.name ?? 'Rootegra LLC',
      members: { leah: m.leah?.name ?? 'Leah', laura: m.laura?.name ?? 'Laura' },
    };
  }, [settings.data]);

  const report: ReportKey = (REPORTS.find((r) => r.key === params.get('report'))?.key ?? 'pl');
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v == null || v === '') next.delete(k); else next.set(k, v); }
    setParams(next);
  };
  const p = {
    from: params.get('from') ?? yearStart(),
    to: params.get('to') ?? today(),
    asof: params.get('asof') ?? today(),
    year: Number(params.get('year') ?? thisYear()),
    account: params.get('account') ?? '',
    opening: params.get('opening') === '1',
    compare: params.get('compare') === '1',
    back: params.get('back'),
  };

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <ErrorNote error={chart.error} />
      <nav className="lp-tabs lp-no-print" aria-label="Reports" style={{ flexWrap: 'wrap', marginBottom: 16 }}>
        {REPORTS.map((r) => (
          <button key={r.key} type="button" className={report === r.key ? 'on' : ''} onClick={() => set({ report: r.key, back: null, account: r.key === 'gl' ? p.account : null })}>{r.label}</button>
        ))}
      </nav>
      {report === 'pl' && <ProfitLossReport entity={entity} from={p.from} to={p.to} compare={p.compare} onChange={set} />}
      {report === 'bs' && <BalanceSheetReport entity={entity} asOf={p.asof} onChange={set} />}
      {report === 'cf' && <CashFlowReport entity={entity} from={p.from} to={p.to} onChange={set} />}
      {report === 'tb' && <TrialBalanceReport entity={entity} from={p.from} to={p.to} opening={p.opening} onChange={set} onOpenAccount={(id) => set({ report: 'gl', account: id, back: 'tb' })} />}
      {report === 'gl' && (
        <GeneralLedgerReport
          entity={entity}
          from={p.from}
          to={p.to}
          accountId={p.account}
          accounts={chart.accounts}
          byId={chart.byId}
          onChange={set}
          back={p.back === 'tb' ? () => set({ report: 'tb', back: null, account: null }) : null}
        />
      )}
      {report === 'capital' && <MemberCapitalReport entity={entity} year={p.year} onChange={set} />}
      {report === 'f1065' && <Form1065Report entity={entity} year={p.year} onChange={set} />}
    </>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────

function ReportHead({ entity, title, range, onCsv, csvDisabled, children }: { entity: Entity; title: string; range: string; onCsv: () => void; csvDisabled?: boolean; children?: ReactNode }) {
  return (
    <>
    {/* The formal statement heading — the only heading that prints. */}
    <div className="bk-stmt-head" aria-hidden="true">
      <div className="bk-stmt-entity">{entity.name}</div>
      <div className="bk-stmt-title">{title}</div>
      <div className="bk-stmt-range">{range}</div>
    </div>
    <div className="bk-stmt-foot" aria-hidden="true">Prepared {day(today())} · Accrual basis · Unaudited</div>
    <div className="lp-section-head lp-no-print" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
      <div>
        <div className="muted">{entity.name}</div>
        <h2 className="lp-section-title" style={{ margin: 0 }}>{title}</h2>
        <div className="muted">{range} · Prepared {day(today())}</div>
      </div>
      <div className="lp-row lp-no-print">
        {children}
        <button type="button" className="lp-btn lp-btn--quiet" onClick={onCsv} disabled={csvDisabled}>CSV</button>
        <PrintButton />
      </div>
    </div>
    </>
  );
}

function RangeControls({ from, to, onChange, extra }: { from: string; to: string; onChange: (patch: Record<string, string | null>) => void; extra?: ReactNode }) {
  const y = thisYear();
  return (
    <div className="lp-row lp-no-print" style={{ marginBottom: 12 }}>
      <Field label="From"><input className="lp-inp" type="date" value={from} onChange={(e) => onChange({ from: e.target.value })} /></Field>
      <Field label="To"><input className="lp-inp" type="date" value={to} onChange={(e) => onChange({ to: e.target.value })} /></Field>
      <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => onChange({ from: `${y}-01-01`, to: today() })}>This year</button>
      <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => onChange({ from: `${y - 1}-01-01`, to: `${y - 1}-12-31` })}>Last year</button>
      {extra}
    </div>
  );
}

function YearControl({ year, onChange }: { year: number; onChange: (y: number) => void }) {
  return (
    <div className="lp-row lp-no-print" style={{ marginBottom: 12 }}>
      <Field label="Year">
        <select className="lp-inp" value={year} onChange={(e) => onChange(Number(e.target.value))}>
          {yearsBack().map((y) => <option key={y} value={y}>{y}</option>)}
          {!yearsBack().includes(year) && <option value={year}>{year}</option>}
        </select>
      </Field>
    </div>
  );
}

function rangeWords(from: string, to: string) { return `${day(from)} to ${day(to)}`; }

function SectionRows({ title, section, prior, onRow }: { title: string; section: PlSection; prior?: Map<string, number> | null; onRow?: (id: string) => void }) {
  if (section.rows.length === 0 && !prior) return null;
  return (
    <>
      <tr className="quiet"><td colSpan={prior ? 3 : 2}><strong>{title}</strong></td></tr>
      {section.rows.map((r) => (
        <tr key={r.account_id}>
          <td style={{ paddingLeft: 28 }}>
            {onRow ? <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" onClick={() => onRow(r.account_id)}><span className="bk-code">{r.code} · </span>{r.name}</button> : <>{r.code ? <span className="bk-code">{r.code} · </span> : null}{r.name}</>}
          </td>
          <td className="num"><Money cents={r.amount_cents} /></td>
          {prior && <td className="num"><Money cents={prior.get(r.account_id) ?? 0} blankZero /></td>}
        </tr>
      ))}
    </>
  );
}

// ── profit and loss ──────────────────────────────────────────────────────────

function ProfitLossReport({ entity, from, to, compare, onChange }: { entity: Entity; from: string; to: string; compare: boolean; onChange: (p: Record<string, string | null>) => void }) {
  const prior = useMemo(() => priorPeriod(from, to), [from, to]);
  const data = useLoad(async () => {
    const [cur, prev] = await Promise.all([
      reportsApi.trialBalance(from, to),
      compare ? reportsApi.trialBalance(prior.from, prior.to) : Promise.resolve<TrialBalanceRow[] | null>(null),
    ]);
    return { pl: buildProfitLoss(cur), prior: prev ? buildProfitLoss(prev) : null };
  }, [from, to, compare]);

  const pl = data.data?.pl ?? null;
  const priorPl = data.data?.prior ?? null;
  const priorMap = useMemo(() => {
    if (!priorPl) return null;
    const m = new Map<string, number>();
    for (const s of [priorPl.income, priorPl.cogs, priorPl.expenses]) for (const r of s.rows) m.set(r.account_id, r.amount_cents);
    return m;
  }, [priorPl]);

  // Rows that only exist in the prior period still need to show.
  const merged = useMemo(() => {
    if (!pl) return null;
    if (!priorPl) return pl;
    const mergeSection = (a: PlSection, b: PlSection): PlSection => {
      const ids = new Set(a.rows.map((r) => r.account_id));
      const extra = b.rows.filter((r) => !ids.has(r.account_id)).map((r) => ({ ...r, amount_cents: 0 }));
      return { rows: [...a.rows, ...extra].sort((x, y) => x.code.localeCompare(y.code)), total_cents: a.total_cents };
    };
    return { ...pl, income: mergeSection(pl.income, priorPl.income), cogs: mergeSection(pl.cogs, priorPl.cogs), expenses: mergeSection(pl.expenses, priorPl.expenses) };
  }, [pl, priorPl]);

  const csv = () => {
    if (!pl) return;
    const head = ['Section', 'Code', 'Account', `${from} to ${to}`, ...(priorPl ? [`${prior.from} to ${prior.to}`] : [])];
    const rows: unknown[][] = [head];
    const push = (title: string, s: PlSection) => {
      for (const r of s.rows) rows.push([title, r.code, r.name, money(r.amount_cents), ...(priorMap ? [money(priorMap.get(r.account_id) ?? 0)] : [])]);
      rows.push([title, '', 'Total', money(s.total_cents), ...(priorPl ? [money(priorPl[title === 'Income' ? 'income' : title === 'Cost of goods sold' ? 'cogs' : 'expenses'].total_cents)] : [])]);
    };
    const src = merged ?? pl;
    push('Income', src.income); push('Cost of goods sold', src.cogs);
    rows.push(['Gross profit', '', '', money(pl.gross_profit_cents), ...(priorPl ? [money(priorPl.gross_profit_cents)] : [])]);
    push('Expenses', src.expenses);
    rows.push(['Net income', '', '', money(pl.net_income_cents), ...(priorPl ? [money(priorPl.net_income_cents)] : [])]);
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-profit-and-loss-${from}-${to}.csv`, rows);
  };

  return (
    <section className="lp-section">
      <ReportHead entity={entity} title="Profit and loss" range={rangeWords(from, to)} onCsv={csv} csvDisabled={!pl} />
      <RangeControls
        from={from}
        to={to}
        onChange={onChange}
        extra={(
          <label className="lp-row" style={{ gap: 6 }}>
            <input type="checkbox" checked={compare} onChange={(e) => onChange({ compare: e.target.checked ? '1' : null })} />
            Compare with the prior period
          </label>
        )}
      />
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : !pl || !merged ? null : (merged.income.rows.length + merged.cogs.rows.length + merged.expenses.rows.length === 0) ? <Empty>No activity in this range.</Empty> : (
        <div className="lp-tbl-wrap">
          <table className="lp-tbl">
            <thead>
              <tr><th>Account</th><th className="num">{rangeWords(from, to)}</th>{priorPl && <th className="num">{rangeWords(prior.from, prior.to)}</th>}</tr>
            </thead>
            <tbody>
              <SectionRows title="Income" section={merged.income} prior={priorMap} />
              <tr className="total"><td>Total income</td><td className="num"><Money cents={pl.income.total_cents} /></td>{priorPl && <td className="num"><Money cents={priorPl.income.total_cents} /></td>}</tr>
              <SectionRows title="Cost of goods sold" section={merged.cogs} prior={priorMap} />
              {(merged.cogs.rows.length > 0) && <tr className="total"><td>Total cost of goods sold</td><td className="num"><Money cents={pl.cogs.total_cents} /></td>{priorPl && <td className="num"><Money cents={priorPl.cogs.total_cents} /></td>}</tr>}
              <tr className="total"><td>Gross profit</td><td className="num"><Money cents={pl.gross_profit_cents} /></td>{priorPl && <td className="num"><Money cents={priorPl.gross_profit_cents} /></td>}</tr>
              <SectionRows title="Expenses" section={merged.expenses} prior={priorMap} />
              <tr className="total"><td>Total expenses</td><td className="num"><Money cents={pl.expenses.total_cents} /></td>{priorPl && <td className="num"><Money cents={priorPl.expenses.total_cents} /></td>}</tr>
              <tr className="total"><td>Net income</td><td className="num"><Money cents={pl.net_income_cents} /></td>{priorPl && <td className="num"><Money cents={priorPl.net_income_cents} /></td>}</tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── balance sheet ────────────────────────────────────────────────────────────

function BalanceSheetTable({ bs }: { bs: BalanceSheet }) {
  return (
    <div className="lp-tbl-wrap">
      <table className="lp-tbl">
        <thead><tr><th>Account</th><th className="num">Balance</th></tr></thead>
        <tbody>
          <SectionRows title="Assets" section={bs.assets} />
          <tr className="total"><td>Total assets</td><td className="num"><Money cents={bs.total_assets_cents} /></td></tr>
          <SectionRows title="Liabilities" section={bs.liabilities} />
          <tr className="total"><td>Total liabilities</td><td className="num"><Money cents={bs.liabilities.total_cents} /></td></tr>
          <SectionRows title="Equity" section={bs.equity} />
          <tr className="total"><td>Total equity</td><td className="num"><Money cents={bs.equity.total_cents} /></td></tr>
          <tr className="total"><td>Total liabilities and equity</td><td className="num"><Money cents={bs.total_liabilities_and_equity_cents} /></td></tr>
        </tbody>
      </table>
    </div>
  );
}

function BalanceSheetReport({ entity, asOf, onChange }: { entity: Entity; asOf: string; onChange: (p: Record<string, string | null>) => void }) {
  const data = useLoad(async () => buildBalanceSheet(await reportsApi.trialBalance(FAR_PAST, asOf)), [asOf]);
  const bs = data.data;
  const csv = () => {
    if (!bs) return;
    const rows: unknown[][] = [['Section', 'Code', 'Account', `As of ${asOf}`]];
    for (const [title, s] of [['Assets', bs.assets], ['Liabilities', bs.liabilities], ['Equity', bs.equity]] as [string, PlSection][]) {
      for (const r of s.rows) rows.push([title, r.code, r.name, money(r.amount_cents)]);
      rows.push([title, '', 'Total', money(s.total_cents)]);
    }
    rows.push(['', '', 'Total liabilities and equity', money(bs.total_liabilities_and_equity_cents)]);
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-balance-sheet-${asOf}.csv`, rows);
  };
  return (
    <section className="lp-section">
      <ReportHead entity={entity} title="Balance sheet" range={`As of ${day(asOf)}`} onCsv={csv} csvDisabled={!bs} />
      <div className="lp-row lp-no-print" style={{ marginBottom: 12 }}>
        <Field label="As of"><input className="lp-inp" type="date" value={asOf} onChange={(e) => onChange({ asof: e.target.value })} /></Field>
        <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => onChange({ asof: today() })}>Today</button>
        <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => onChange({ asof: `${thisYear() - 1}-12-31` })}>End of last year</button>
      </div>
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : !bs ? null : bs.assets.rows.length + bs.liabilities.rows.length === 0 && bs.net_income_to_date_cents === 0 ? <Empty>Nothing on the books as of this date.</Empty> : (
        <>
          {!bs.balanced && <Note tone="warn">Assets do not equal liabilities plus equity by <Money cents={bs.total_assets_cents - bs.total_liabilities_and_equity_cents} />. Check the Journal for an entry that did not balance.</Note>}
          <BalanceSheetTable bs={bs} />
          {bs.balanced && <p className="muted">Balanced. Net income to date is income less cost of goods and expenses through {day(asOf)}.</p>}
        </>
      )}
    </section>
  );
}

// ── cash flow ────────────────────────────────────────────────────────────────

function CashRows({ title, s }: { title: string; s: CashFlowSection }) {
  return (
    <>
      <tr className="quiet"><td colSpan={2}><strong>{title}</strong></td></tr>
      {s.rows.map((r) => <tr key={r.label}><td style={{ paddingLeft: 28 }}>{r.label}</td><td className="num"><Money cents={r.amount_cents} blankZero /></td></tr>)}
      <tr className="total"><td>Net cash from {title.toLowerCase()}</td><td className="num"><Money cents={s.total_cents} /></td></tr>
    </>
  );
}

function CashFlowReport({ entity, from, to, onChange }: { entity: Entity; from: string; to: string; onChange: (p: Record<string, string | null>) => void }) {
  const data = useLoad(async () => {
    const [period, start, end] = await Promise.all([
      reportsApi.trialBalance(from, to),
      reportsApi.trialBalance(FAR_PAST, addDays(from, -1)),
      reportsApi.trialBalance(FAR_PAST, to),
    ]);
    return buildCashFlow(buildProfitLoss(period), buildBalanceSheet(start), buildBalanceSheet(end));
  }, [from, to]);
  const cf = data.data;
  const csv = () => {
    if (!cf) return;
    const rows: unknown[][] = [['Section', 'Line', `${from} to ${to}`]];
    for (const [title, s] of [['Operating activities', cf.operating], ['Investing activities', cf.investing], ['Financing activities', cf.financing]] as [string, CashFlowSection][]) {
      for (const r of s.rows) rows.push([title, r.label, money(r.amount_cents)]);
      rows.push([title, 'Total', money(s.total_cents)]);
    }
    rows.push(['', 'Net change in cash', money(cf.net_change_cents)], ['', 'Cash at start', money(cf.beginning_cash_cents)], ['', 'Cash at end', money(cf.ending_cash_cents)]);
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-cash-flow-${from}-${to}.csv`, rows);
  };
  return (
    <section className="lp-section">
      <ReportHead entity={entity} title="Statement of cash flows" range={rangeWords(from, to)} onCsv={csv} csvDisabled={!cf} />
      <RangeControls from={from} to={to} onChange={onChange} />
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : !cf ? null : (
        <>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Line</th><th className="num">{rangeWords(from, to)}</th></tr></thead>
              <tbody>
                <CashRows title="Operating activities" s={cf.operating} />
                <CashRows title="Investing activities" s={cf.investing} />
                <CashRows title="Financing activities" s={cf.financing} />
                <tr className="total"><td>Net change in cash</td><td className="num"><Money cents={cf.net_change_cents} /></td></tr>
                <tr><td>Cash at start, {day(addDays(from, -1))}</td><td className="num"><Money cents={cf.beginning_cash_cents} /></td></tr>
                <tr className="total"><td>Cash at end, {day(to)}</td><td className="num"><Money cents={cf.ending_cash_cents} /></td></tr>
              </tbody>
            </table>
          </div>
          {cf.ties
            ? <p className="muted"><span className="lp-tag lp-tag--good">Ties</span> Cash at start plus the net change equals cash at end.</p>
            : <Note tone="warn">The statement does not tie: cash at start plus the net change is off from cash at end by <Money cents={cf.beginning_cash_cents + cf.net_change_cents - cf.ending_cash_cents} />. Check the balance sheet at each end.</Note>}
        </>
      )}
    </section>
  );
}

// ── trial balance ────────────────────────────────────────────────────────────

function TrialBalanceReport({ entity, from, to, opening, onChange, onOpenAccount }: {
  entity: Entity; from: string; to: string; opening: boolean;
  onChange: (p: Record<string, string | null>) => void; onOpenAccount: (id: string) => void;
}) {
  const data = useLoad(async () => buildTrialBalance(await reportsApi.trialBalance(from, to), { includeOpening: opening }), [from, to, opening]);
  const tb = data.data;
  const csv = () => {
    if (!tb) return;
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-trial-balance-${from}-${to}.csv`, [
      ['Code', 'Account', 'Debit', 'Credit'],
      ...tb.rows.map((r) => [r.code, r.name, money(r.debit_cents), money(r.credit_cents)]),
      ['', 'Total', money(tb.total_debit_cents), money(tb.total_credit_cents)],
    ]);
  };
  return (
    <section className="lp-section">
      <ReportHead entity={entity} title="Trial balance" range={`${rangeWords(from, to)}${opening ? ', with opening balances' : ''}`} onCsv={csv} csvDisabled={!tb} />
      <RangeControls
        from={from}
        to={to}
        onChange={onChange}
        extra={(
          <label className="lp-row" style={{ gap: 6 }}>
            <input type="checkbox" checked={opening} onChange={(e) => onChange({ opening: e.target.checked ? '1' : null })} />
            Include balances from before the range
          </label>
        )}
      />
      <p className="muted">Choose an account to open its ledger for the same range.</p>
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : !tb ? null : tb.rows.length === 0 ? <Empty>No balances in this range.</Empty> : (
        <>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
              <tbody>
                {tb.rows.map((r) => (
                  <tr key={r.account_id}>
                    <td><button type="button" className="lp-btn lp-btn--quiet lp-btn--small" onClick={() => onOpenAccount(r.account_id)}>{r.code} · {r.name}</button></td>
                    <td className="num"><Money cents={r.debit_cents} blankZero /></td>
                    <td className="num"><Money cents={r.credit_cents} blankZero /></td>
                  </tr>
                ))}
                <tr className="total"><td>Total</td><td className="num"><Money cents={tb.total_debit_cents} /></td><td className="num"><Money cents={tb.total_credit_cents} /></td></tr>
              </tbody>
            </table>
          </div>
          {tb.balanced
            ? <p className="muted"><span className="lp-tag lp-tag--good">Balanced</span> Debits equal credits.</p>
            : <Note tone="warn">Debits and credits differ by <Money cents={tb.total_debit_cents - tb.total_credit_cents} />. Check the Journal for an entry that did not balance.</Note>}
        </>
      )}
    </section>
  );
}

// ── general ledger ───────────────────────────────────────────────────────────

const TYPE_ORDER: AccountType[] = ['asset', 'liability', 'equity', 'income', 'cogs', 'expense'];

function GeneralLedgerReport({ entity, from, to, accountId, accounts, byId, onChange, back }: {
  entity: Entity; from: string; to: string; accountId: string; accounts: Account[]; byId: Map<string, Account>;
  onChange: (p: Record<string, string | null>) => void; back: (() => void) | null;
}) {
  const account = accountId ? byId.get(accountId) ?? null : null;
  const data = useLoad(async () => {
    const [lines, tb] = await Promise.all([
      reportsApi.ledger(from, to, accountId || null),
      accountId ? reportsApi.trialBalance(from, to) : Promise.resolve<TrialBalanceRow[]>([]),
    ]);
    const row = tb.find((r) => r.account_id === accountId) ?? null;
    const opening = row ? (row.normal_balance === 'credit' ? Number(row.opening_credit_cents) - Number(row.opening_debit_cents) : Number(row.opening_debit_cents) - Number(row.opening_credit_cents)) : 0;
    return { lines, opening, normal: row?.normal_balance ?? 'debit' };
  }, [from, to, accountId]);
  const lines: LedgerLine[] = data.data?.lines ?? [];
  const balances = useMemo(() => (account && data.data ? runningBalances(lines, data.data.normal, data.data.opening) : null), [account, data.data, lines]);

  const csv = () => {
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-general-ledger-${from}-${to}${account ? `-${account.code}` : ''}.csv`, [
      ['Date', 'Entry', 'Memo', 'Source', 'Code', 'Account', 'Line memo', 'Debit', 'Credit', ...(balances ? ['Balance'] : [])],
      ...lines.map((l, i) => [l.entry_date, l.entry_id, l.memo ?? '', SOURCE_LABELS[l.source] ?? l.source, l.code, l.name, l.line_memo ?? '', money(Number(l.debit_cents)), money(Number(l.credit_cents)), ...(balances ? [money(balances[i])] : [])]),
    ]);
  };

  return (
    <section className="lp-section">
      {back && <BackTo label="Trial balance" onClick={back} />}
      <ReportHead entity={entity} title={account ? `General ledger · ${account.code} ${account.name}` : 'General ledger'} range={rangeWords(from, to)} onCsv={csv} csvDisabled={lines.length === 0} />
      <RangeControls
        from={from}
        to={to}
        onChange={onChange}
        extra={(
          <Field label="Account">
            <select className="lp-inp" value={accountId} onChange={(e) => onChange({ account: e.target.value || null })}>
              <option value="">All accounts</option>
              {TYPE_ORDER.map((t) => {
                const rows = accounts.filter((a) => a.type === t && (a.is_active || a.id === accountId));
                return rows.length ? (
                  <optgroup key={t} label={ACCOUNT_TYPE_LABELS[t]}>
                    {rows.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                  </optgroup>
                ) : null;
              })}
            </select>
          </Field>
        )}
      />
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : lines.length === 0 ? <Empty>No lines in this range{account ? ' for this account' : ''}.</Empty> : (
        <div className="lp-tbl-wrap">
          <table className="lp-tbl">
            <thead>
              <tr><th>Date</th><th>Memo</th><th>Source</th>{!account && <th>Account</th>}<th className="num">Debit</th><th className="num">Credit</th>{balances && <th className="num">Balance</th>}</tr>
            </thead>
            <tbody>
              {account && (
                <tr className="quiet"><td>{day(from)}</td><td>Opening balance</td><td /><td /><td /><td className="num"><Money cents={data.data?.opening ?? 0} /></td></tr>
              )}
              {lines.map((l, i) => (
                <tr key={l.line_id} className={l.is_reversed ? 'quiet' : undefined}>
                  <td>{day(l.entry_date)}</td>
                  <td>
                    <div>{l.memo || '—'}{l.is_reversed && <span className="lp-tag lp-tag--warn" style={{ marginLeft: 6 }}>Reversed</span>}</div>
                    {l.line_memo && <div className="muted">{l.line_memo}</div>}
                  </td>
                  <td>{SOURCE_LABELS[l.source] ?? l.source}</td>
                  {!account && <td>{l.code} · {l.name}</td>}
                  <td className="num"><Money cents={Number(l.debit_cents)} blankZero /></td>
                  <td className="num"><Money cents={Number(l.credit_cents)} blankZero /></td>
                  {balances && <td className="num"><Money cents={balances[i]} /></td>}
                </tr>
              ))}
              <tr className="total">
                <td>Total</td><td /><td />{!account && <td />}
                <td className="num"><Money cents={lines.reduce((s, l) => s + Number(l.debit_cents), 0)} /></td>
                <td className="num"><Money cents={lines.reduce((s, l) => s + Number(l.credit_cents), 0)} /></td>
                {balances && <td className="num"><Money cents={balances[balances.length - 1] ?? data.data?.opening ?? 0} /></td>}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── member capital ───────────────────────────────────────────────────────────

function MemberCapitalReport({ entity, year, onChange }: { entity: Entity; year: number; onChange: (p: Record<string, string | null>) => void }) {
  const data = useLoad(() => membersApi.capital(year), [year]);
  const cap = data.data;
  const csv = () => {
    if (!cap) return;
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-member-capital-${year}.csv`, [
      ['Member', 'Share %', 'Beginning', 'Contributions', 'Allocated income', 'Distributions', 'Ending'],
      ...MEMBERS.map((m) => { const r = cap.members[m]; return [entity.members[m], r.pct, money(r.beginning_cents), money(r.contributions_cents), money(r.allocated_income_cents), money(r.distributions_cents), money(r.ending_cents)]; }),
      ['Net income', '', '', '', money(cap.net_income_cents), '', ''],
    ]);
  };
  return (
    <section className="lp-section">
      <ReportHead entity={entity} title="Member capital" range={`Year ${year}`} onCsv={csv} csvDisabled={!cap} />
      <YearControl year={year} onChange={(y) => onChange({ year: String(y) })} />
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : !cap ? null : (
        <>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Member</th><th className="num">Share</th><th className="num">Beginning</th><th className="num">Contributions</th><th className="num">Allocated income</th><th className="num">Distributions</th><th className="num">Ending</th></tr></thead>
              <tbody>
                {MEMBERS.map((m) => {
                  const r = cap.members[m];
                  return (
                    <tr key={m}>
                      <td>{entity.members[m]}</td>
                      <td className="num">{r.pct}%</td>
                      <td className="num"><Money cents={r.beginning_cents} /></td>
                      <td className="num"><Money cents={r.contributions_cents} blankZero /></td>
                      <td className="num"><Money cents={r.allocated_income_cents} /></td>
                      <td className="num"><Money cents={-Number(r.distributions_cents)} blankZero /></td>
                      <td className="num"><Money cents={r.ending_cents} /></td>
                    </tr>
                  );
                })}
                <tr className="total">
                  <td>Total</td><td className="num">{MEMBERS.reduce((s, m) => s + Number(cap.members[m].pct), 0)}%</td>
                  <td className="num"><Money cents={MEMBERS.reduce((s, m) => s + Number(cap.members[m].beginning_cents), 0)} /></td>
                  <td className="num"><Money cents={MEMBERS.reduce((s, m) => s + Number(cap.members[m].contributions_cents), 0)} /></td>
                  <td className="num"><Money cents={cap.net_income_cents} /></td>
                  <td className="num"><Money cents={-MEMBERS.reduce((s, m) => s + Number(cap.members[m].distributions_cents), 0)} /></td>
                  <td className="num"><Money cents={MEMBERS.reduce((s, m) => s + Number(cap.members[m].ending_cents), 0)} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="muted">Allocated income is net income split by each member's share; {entity.members.leah}'s part is rounded to the cent and {entity.members.laura} takes the remainder. Beginning is the balance carried from the prior year.</p>
        </>
      )}
    </section>
  );
}

// ── Form 1065 ────────────────────────────────────────────────────────────────

function ScheduleLRow({ label, begin, end }: { label: string; begin: number; end: number }) {
  return <tr><td>{label}</td><td className="num"><Money cents={begin} /></td><td className="num"><Money cents={end} /></td></tr>;
}

function Form1065Report({ entity, year, onChange }: { entity: Entity; year: number; onChange: (p: Record<string, string | null>) => void }) {
  const data = useLoad(async () => {
    const [tb, cap, shares, begin, end] = await Promise.all([
      reportsApi.trialBalance(`${year}-01-01`, `${year}-12-31`),
      membersApi.capital(year),
      settingsApi.shares(),
      reportsApi.trialBalance(FAR_PAST, `${year - 1}-12-31`),
      reportsApi.trialBalance(FAR_PAST, `${year}-12-31`),
    ]);
    return build1065(tb, cap, shares, buildBalanceSheet(begin), buildBalanceSheet(end));
  }, [year]);
  const f: Form1065 | null = data.data;

  const csv = () => {
    if (!f) return;
    const rows: unknown[][] = [['Part', 'Line', 'Amount']];
    for (const l of f.page1.lines) rows.push(['Page 1', l.label, money(l.amount_cents)]);
    rows.push(['Page 1', 'Total income (loss)', money(f.page1.total_income_cents)], ['Page 1', 'Total deductions', money(f.page1.total_deductions_cents)], ['Page 1', 'Ordinary business income (loss)', money(f.page1.ordinary_income_cents)]);
    rows.push(['Schedule K', 'Ordinary business income (loss)', money(f.schedule_k.ordinary_income_cents)], ['Schedule K', 'Distributions', money(f.schedule_k.distributions_cents)]);
    for (const m of MEMBERS) {
      const k = f.k1[m];
      rows.push([`K-1 ${entity.members[m]}`, 'Profit share %', k.pct], [`K-1 ${entity.members[m]}`, 'Ordinary business income (loss)', money(k.ordinary_income_cents)], [`K-1 ${entity.members[m]}`, 'Distributions', money(k.distributions_cents)],
        [`K-1 ${entity.members[m]}`, 'Beginning capital', money(k.capital.beginning_cents)], [`K-1 ${entity.members[m]}`, 'Capital contributed', money(k.capital.contributions_cents)], [`K-1 ${entity.members[m]}`, 'Current year net income (loss)', money(k.capital.allocated_income_cents)], [`K-1 ${entity.members[m]}`, 'Withdrawals and distributions', money(k.capital.distributions_cents)], [`K-1 ${entity.members[m]}`, 'Ending capital', money(k.capital.ending_cents)]);
    }
    const L = (label: string, pick: (s: BalanceSheetSummary) => number) => rows.push(['Schedule L', label, `${money(pick(f.schedule_l.begin))} → ${money(pick(f.schedule_l.end))}`]);
    L('Cash', (s) => s.cash_cents); L('Total assets', (s) => s.total_assets_cents); L('Total liabilities', (s) => s.total_liabilities_cents); L('Partners\' capital', (s) => s.total_equity_cents);
    downloadCsv(`${entity.name.replace(/\s+/g, '-')}-form-1065-summary-${year}.csv`, rows);
  };

  return (
    <section className="lp-section">
      <ReportHead entity={entity} title="Form 1065 summary" range={`Tax year ${year}`} onCsv={csv} csvDisabled={!f} />
      <YearControl year={year} onChange={(y) => onChange({ year: String(y) })} />
      <Note>Line numbers follow the 2025 Form 1065. Check line numbers against the current year's form. This is a summary for the accountant, not the return.</Note>
      <ErrorNote error={data.error} />
      {data.loading ? <Empty>Loading…</Empty> : !f ? null : (
        <>
          {f.untagged.length > 0 && (
            <Note tone="warn">
              {f.untagged.length === 1 ? 'One account has' : `${f.untagged.length} accounts have`} activity but no tax line, so page 1 leaves {f.untagged.length === 1 ? 'it' : 'them'} out: {f.untagged.map((r) => `${r.code} ${r.name}`).join(', ')}. Set a tax line under Settings.
            </Note>
          )}
          <h3>Page 1 · Income and deductions</h3>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Line</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {f.page1.lines.slice(0, 4).map((l) => <tr key={l.tax_line}><td>{l.label}</td><td className="num"><Money cents={l.amount_cents} blankZero /></td></tr>)}
                <tr className="total"><td>8 Total income (loss)</td><td className="num"><Money cents={f.page1.total_income_cents} /></td></tr>
                {f.page1.lines.slice(4).map((l) => <tr key={l.tax_line}><td>{l.label}</td><td className="num"><Money cents={l.amount_cents} blankZero /></td></tr>)}
                <tr className="total"><td>22 Total deductions</td><td className="num"><Money cents={f.page1.total_deductions_cents} /></td></tr>
                <tr className="total"><td>23 Ordinary business income (loss)</td><td className="num"><Money cents={f.page1.ordinary_income_cents} /></td></tr>
              </tbody>
            </table>
          </div>

          <h3>Schedule K · Partners' distributive share items</h3>
          <dl className="lp-kv">
            <dt>1 Ordinary business income (loss)</dt><dd><Money cents={f.schedule_k.ordinary_income_cents} /></dd>
            <dt>19a Distributions of cash</dt><dd><Money cents={f.schedule_k.distributions_cents} /></dd>
          </dl>

          <h3>Schedule K-1 · One per member</h3>
          <div className="kpi-grid">
            {MEMBERS.map((m) => {
              const k = f.k1[m];
              return (
                <div key={m} className="lp-panel">
                  <h4 style={{ marginTop: 0 }}>{entity.members[m]} · {k.pct}%</h4>
                  <dl className="lp-kv">
                    <dt>1 Ordinary business income (loss)</dt><dd><Money cents={k.ordinary_income_cents} /></dd>
                    <dt>19 Distributions</dt><dd><Money cents={k.distributions_cents} /></dd>
                    <dt>L Beginning capital</dt><dd><Money cents={k.capital.beginning_cents} /></dd>
                    <dt>L Capital contributed</dt><dd><Money cents={k.capital.contributions_cents} /></dd>
                    <dt>L Current year net income (loss)</dt><dd><Money cents={k.capital.allocated_income_cents} /></dd>
                    <dt>L Withdrawals and distributions</dt><dd><Money cents={-Number(k.capital.distributions_cents)} /></dd>
                    <dt>L Ending capital</dt><dd><Money cents={k.capital.ending_cents} /></dd>
                  </dl>
                </div>
              );
            })}
          </div>

          <h3>Schedule L · Balance sheets per books</h3>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th /><th className="num">Beginning of year</th><th className="num">End of year</th></tr></thead>
              <tbody>
                <ScheduleLRow label="Cash" begin={f.schedule_l.begin.cash_cents} end={f.schedule_l.end.cash_cents} />
                <ScheduleLRow label="Total assets" begin={f.schedule_l.begin.total_assets_cents} end={f.schedule_l.end.total_assets_cents} />
                <ScheduleLRow label="Total liabilities" begin={f.schedule_l.begin.total_liabilities_cents} end={f.schedule_l.end.total_liabilities_cents} />
                <ScheduleLRow label="Partners' capital" begin={f.schedule_l.begin.total_equity_cents} end={f.schedule_l.end.total_equity_cents} />
                <tr className="total">
                  <td>Total liabilities and capital</td>
                  <td className="num"><Money cents={f.schedule_l.begin.total_liabilities_cents + f.schedule_l.begin.total_equity_cents} /></td>
                  <td className="num"><Money cents={f.schedule_l.end.total_liabilities_cents + f.schedule_l.end.total_equity_cents} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="muted">Open the Balance sheet report as of {day(`${year - 1}-12-31`)} or {day(`${year}-12-31`)} for every account behind these totals.</p>
        </>
      )}
    </section>
  );
}
